class CompareUtils {
  static cookieName = 'hypertheme:compare-products';

  /**
   *
   * @returns {array}
   */
  static getSelectedProducts() {
    const products = FoxTheme.utils.getStorage(this.cookieName);
    return products || [];
  }

  static setSelectedProducts(products) {
    if (products.length > 0) {
      FoxTheme.utils.setStorage(this.cookieName, products);
    } else {
      localStorage.removeItem(this.cookieName);
    }
  }

  static getProductsCount() {
    return this.getSelectedProducts().length;
  }

  /**
   * Check the given product id in compare list.
   * @param {*} productId
   * @returns {boolean}
   */
  static isProductInList(productId) {
    const products = this.getSelectedProducts();

    return products.find((product) => product.id === productId);
  }

  /**
   *
   * @param {*} productId
   * @param {*} productUrl
   * @returns {boolean} True if the product added to list.
   */
  static addToList(productId, productUrl, group = '') {
    let products = FoxTheme.utils.getStorage(this.cookieName) || [];

    // LSM: one comparison holds cameras or lenses, never both (David, 2026-10-19); the checkbox asks before starting over
    if (this.otherGroup(group)) return false;

    if (products.length < FoxTheme.compare.maxProductsInCompare) {
      if (!products.some((product) => product.id === productId)) {
        products.push({ id: productId, url: productUrl, group: group });
      }

      this.setSelectedProducts(products);
      this.updateCompareCheckboxes(products);

      FoxTheme.pubsub.publish('compare:add', { products: products, productId: productId, productUrl: productUrl });

      return true;
    } else {
      alert(FoxTheme.compare.alertMessage);

      return false;
    }
  }

  /** LSM: the group already being compared when it differs from this one ("camera" or "lens"), else '' */
  static otherGroup(group) {
    if (!group) return '';
    const other = this.getSelectedProducts().find((product) => product.group && product.group !== group);
    return other ? other.group : '';
  }

  /** LSM: a question in the store's own style instead of the browser's confirm box (David, 2026-10-19). Resolves true or false. */
  static ask(text, yesLabel, noLabel) {
    return new Promise((resolve) => {
      let d = document.getElementById('LsmCompareAsk');
      if (!d) {
        d = document.createElement('dialog');
        d.id = 'LsmCompareAsk';
        d.className = 'lsm-ask';
        d.innerHTML = '<p class="lsm-ask__text"></p><div class="lsm-ask__btns"><button type="button" class="btn btn--secondary"></button><button type="button" class="btn btn--primary"></button></div>';
        document.body.appendChild(d);
      }
      const [no, yes] = d.querySelectorAll('button');
      d.querySelector('.lsm-ask__text').textContent = text;
      no.textContent = noLabel;
      yes.textContent = yesLabel;
      const done = (answer) => { d.close(); resolve(answer); };
      no.onclick = () => done(false);
      yes.onclick = () => done(true);
      d.oncancel = (e) => { e.preventDefault(); done(false); };
      d.onclick = (e) => { if (e.target === d) done(false); };
      d.showModal();
      yes.focus();
    });
  }

  static removeFromList(productId) {
    let products = this.getSelectedProducts();
    if (products) {
      products = products.filter((product) => product.id !== productId);
    }
    this.setSelectedProducts(products);
    this.updateCompareCheckboxes(products);
    FoxTheme.pubsub.publish('compare:remove', { products: products, productId: productId });
  }

  static clearList() {
    const products = this.getSelectedProducts();
    if (products) {
      products.forEach((product) => {
        this.removeFromList(product.id);
      });
    }
  }

  static updateCompareCheckboxes(products = null) {
    products = products || this.getSelectedProducts();
    document.querySelectorAll('.js-compare-checkbox').forEach((checkbox) => {
      checkbox.checked = products.filter((product) => product.id === checkbox.dataset.productId).length;
    });
  }

  static getTemplateUrl(productUrl, queryObj = {}) {
    let url = productUrl;
    const separator = productUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();

    for (const key in queryObj) {
      if (Object.prototype.hasOwnProperty.call(queryObj, key)) {
        params.append(key, queryObj[key]);
      }
    }

    // Append parameters to the URL if any were added.
    if (Array.from(params).length > 0) {
      url += `${separator}${params.toString()}`;
    }

    return url;
  }

  static getCompareBarItemTemplate(productUrl) {
    return this.getTemplateUrl(productUrl, {
      sections: 'product-compare-bar-item'
    });
  }
}

class CompareSwitch extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'hypertheme:compare-is-active';

    this.classes = {
      isActive: 'is-product-comparing'
    };

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    if (FoxTheme.config.hasLocalStorage) {
      const isActive = window.localStorage.getItem(this.cookieName) === 'true';
      if (isActive) {
        this.checkboxEl.checked = isActive;
        this.handleChange(isActive);
      }
    }
  }

  onChange(evt) {
    evt.preventDefault();

    this.handleChange(this.checkboxEl.checked);
  }

  handleChange(isActive) {
    requestAnimationFrame(() => {
      if (isActive) {
        document.body.classList.add(this.classes.isActive);
      } else {
        document.body.classList.remove(this.classes.isActive);
      }
    });

    FoxTheme.pubsub.publish('compare:toggle', { isActive: isActive });

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, isActive);
    }
  }
}
customElements.define('compare-switch', CompareSwitch);

class CompareCheckbox extends HTMLElement {
  constructor() {
    super();

    this.checkboxEl = this.querySelector('input');
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.init();
    this.checkboxEl.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.checkboxEl.removeEventListener('change', this.onChangeHandler);
  }

  init() {
    const { productId } = this.checkboxEl.dataset;
    this.checkboxEl.checked = CompareUtils.isProductInList(productId);
  }

  onChange(evt) {
    evt.preventDefault();

    const { productId, productUrl, compareGroup } = this.checkboxEl.dataset;

    if (this.checkboxEl.checked) {
      const other = CompareUtils.otherGroup(compareGroup || '');
      if (other) {
        this.checkboxEl.checked = false;
        const word = (g) => (g === 'lens' ? 'lenses' : 'cameras');
        CompareUtils.ask(`You are comparing ${word(other)}. Start a new comparison of ${word(compareGroup)}?`, `Compare ${word(compareGroup)}`, `Keep ${word(other)}`).then((yes) => {
          if (!yes) return;
          CompareUtils.clearList();
          if (CompareUtils.addToList(productId, productUrl, compareGroup)) this.checkboxEl.checked = true;
        });
        return;
      }
      const isAdded = CompareUtils.addToList(productId, productUrl, compareGroup || '');
      if (!isAdded) {
        this.checkboxEl.checked = false;
      }
    } else {
      CompareUtils.removeFromList(productId);
    }
  }
}
customElements.define('compare-checkbox', CompareCheckbox);

class CompareRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.clickHandler = this.onClick.bind(this);
    this.addEventListener('click', this.clickHandler);
  }

  onClick(event) {
    event.preventDefault();

    const { productId } = this.dataset;
    const { target } = event;

    CompareUtils.removeFromList(productId);

    const compareModal = target.closest('.compare-drawer');
    if (compareModal) {
      compareModal.updateProductsCount();
    }

    if (CompareUtils.getProductsCount() < 2) {
      compareModal && compareModal.hide();
    } else {
      const compareTable = target.closest('.compare-table');
      if (compareTable) {
        const cell = target.closest('td');
        const colIndex = cell.cellIndex;

        const rows = compareTable.querySelectorAll('tr');
        rows.forEach((row) => {
          const cellToRemove = row.cells[colIndex];
          if (cellToRemove) {
            cellToRemove.remove();
          }
        });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }
}
customElements.define('compare-remove', CompareRemove, { extends: 'button' });

class CompareBar extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      openBtn: '.compare-bar__open',
      clearBtns: ['.compare-bar__clear'],
      toggleList: '.compare-bar__toggle-list',
      list: '.compare-bar__list',
      counter: '.compare-bar__counter'
    };

    this.classes = {
      listVisible: 'is-show-list'
    };

    this.abortController = new AbortController();
    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    this.atLeastItemToShow = Math.min(5, FoxTheme.compare.maxProductsInCompare); // Total compare products and placeholders.

    this.elements.clearBtns.forEach((clearBtn) => {
      clearBtn.addEventListener('click', this.handleClear.bind(this), {
        signal: this.abortController.signal
      });
    });

    this.elements.toggleList.addEventListener('click', this.handleToggleList.bind(this), {
      signal: this.abortController.signal
    });

    this.itemAddUnsubscriber = FoxTheme.pubsub.subscribe('compare:add', this.onItemAdded.bind(this));
    this.itemRemoveUnsubscriber = FoxTheme.pubsub.subscribe('compare:remove', this.onItemRemoved.bind(this));
  }

  itemAddUnsubscriber = undefined;
  itemRemoveUnsubscriber = undefined;

  connectedCallback() {
    this.renderBar();
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.onchange = this.setHeight.bind(this);
    this.setHeight();
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.itemAddUnsubscriber) {
      this.itemAddUnsubscriber();
    }

    if (this.itemRemoveUnsubscriber) {
      this.itemRemoveUnsubscriber();
    }
  }

  onItemAdded(event) {
    const { products, productId, productUrl } = event;

    this.appendBarItem(products, productId, productUrl);
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
  }

  onItemRemoved(event) {
    const { products, productId } = event;
    const itemToDelete = this.elements.list.querySelector(`.compare-bar__item[data-product-id="${productId}"]`);

    itemToDelete && itemToDelete.remove();
    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
    this.removePlaceholdes();
    this.addPlaceholders(products.length);
  }

  async renderBar() {
    const products = CompareUtils.getSelectedProducts();

    let output = '';
    const compareItemHtmls = await this.getBarItems(products);
    compareItemHtmls.forEach((htmlStr) => {
      output += htmlStr;
    });

    this.removePlaceholdes();
    this.elements.list.innerHTML = output;
    this.addPlaceholders(products.length);

    this.toggleCompareButton(products.length);
    this.updateCounters(products.length);
  }

  async getBarItems(products) {
    const tmps = [];
    await Promise.all(
      products.map(async (product) => {
        const fetchUrl = CompareUtils.getCompareBarItemTemplate(product.url);
        await fetch(CompareUtils.getCompareBarItemTemplate(product.url))
          .then((response) => response.json())
          .then((response) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
            const itemHtml = doc.querySelector('.shopify-section');

            tmps[product.id] = itemHtml.innerHTML;
          });
      })
    );

    const returns = [];
    products.forEach((product) => returns.push(tmps[product.id]));
    return returns;
  }

  async appendBarItem(products, productId, productUrl) {
    this.removePlaceholdes();
    this.elements.list.insertAdjacentHTML(
      'beforeend',
      `<li class="compare-bar__item" data-product-id="${productId}"></li>`
    );
    this.addPlaceholders(products.length);

    await fetch(CompareUtils.getCompareBarItemTemplate(productUrl))
      .then((response) => response.json())
      .then((response) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(response['product-compare-bar-item'], 'text/html');
        const itemHtml = doc.querySelector('.shopify-section');

        const itemEl = this.elements.list.querySelector('[data-product-id="' + productId + '"]');
        if (itemEl) {
          itemEl.outerHTML = itemHtml.innerHTML;
        }
      });
  }

  handleToggleList(event) {
    event.preventDefault();

    if (this.classList.contains(this.classes.listVisible)) {
      this.classList.remove(this.classes.listVisible);
    } else {
      this.classList.add(this.classes.listVisible);
    }

    this.setHeight();
  }

  handleClear() {
    CompareUtils.clearList();
  }

  toggleCompareButton(count) {
    this.elements.openBtn.disabled = !(count > 1);
  }

  updateCounters(count) {
    this.elements.counter.innerText = count;
  }

  removePlaceholdes() {
    Array.from(this.elements.list.querySelectorAll('li')).forEach((li) => {
      if (li.classList.contains('compare-bar__item-placeholder')) {
        li.remove();
      }
    });
  }

  addPlaceholders(count) {
    const placeholderCount = this.atLeastItemToShow - count;

    for (let i = 0; i < placeholderCount; i++) {
      const placeholderItem = document.createElement('li');

      placeholderItem.classList.add('compare-bar__item', 'compare-bar__item-placeholder');
      this.elements.list.appendChild(placeholderItem);
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      const offsetHeight = Math.round(this.offsetHeight);
      document.documentElement.style.setProperty('--compare-bar-height', `${offsetHeight}px`);
    });
  }
}
customElements.define('compare-bar', CompareBar);

class CompareDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      table: '.compare-table'
    };

    this.classesToRemoveOnLoad = 'drawer--loading';
  }

  get requiresBodyAppended() {
    return false;
  }

  get selector() {
    return '.product-comparison-table';
  }

  get sourceSelector() {
    return '#MainProduct-compare__rows';
  }

  get placeholderSelector() {
    return '#MainProduct-compare__placeholder';
  }

  getSectionId() {
    let sectionId = FoxTheme.ProductCompareSectionId || false;

    if (!sectionId) {
      // Get section id from overlay groups.
      const sectionEl = document.querySelector('.section-group-overlay-product-compare');
      if (sectionEl) {
        sectionId = FoxTheme.utils.getSectionId(sectionEl);
      }

      // Cache for better performance.
      FoxTheme.ProductCompareSectionId = sectionId;
    }

    return sectionId;
  }

  // shouleBeShow() {
  //   const sectionId = this.getSectionId();
  //   return typeof sectionId === 'string';
  // }

  prepareToShow() {
    super.prepareToShow();
    this.renderCompareTable();
  }

  async renderCompareTable() {
    const drawerContent = this.querySelector(this.selector);
    const sectionId = this.getSectionId();

    const products = CompareUtils.getSelectedProducts();

    this.updateProductsCount();

    this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
    const tableRows = this.elements.table.querySelectorAll('tbody tr');
    let compareRows = await this.getCompareTableRows(products);

    if (Shopify.designMode) {
      // Append placeholder items in editor mode.
      let maxPlaceholderItems = 3;

      if (compareRows.length == 2) {
        maxPlaceholderItems = 2;
      }

      let numberPlaceholderItems =
        compareRows.length > 0 ? maxPlaceholderItems - compareRows.length : maxPlaceholderItems;

      if (numberPlaceholderItems > 0) {
        const placeholderRows = this.getCompareTablePlaceholderRows(numberPlaceholderItems);

        compareRows = compareRows.concat(placeholderRows);

        this.updateProductsCount(maxPlaceholderItems);
      }
    }

    compareRows.forEach((compareRow) => {
      tableRows.forEach((row) => {
        const compareKey = row.dataset.compareKey;
        const compareCol = compareRow.querySelector(`td[data-compare-key="${compareKey}"]`);
        row.appendChild(compareCol);
      });
    });

    this.lsmSpecs();

    // LSM: nothing could be shown (every saved product has gone): say so instead of an empty table
    this.querySelector('.lsm-compare-empty')?.remove();
    if (!compareRows.length && !Shopify.designMode) {
      const note = document.createElement('p');
      note.className = 'lsm-compare-empty text-center';
      note.textContent = 'These products are no longer available to compare. Choose others from a collection page.';
      this.elements.table.before(note);
    }
    this.elements.table.hidden = !compareRows.length && !Shopify.designMode;

    setTimeout(() => {
      this.classList.remove(this.classesToRemoveOnLoad);
    }, 300);
  }

  /* LSM: Leica's specifications, one row each (David, 2026-10-19: "publish the specs from OSKAR for the compare table").
     Every column carries its product's rows (metafield lsm.specs, written from OSKAR's Leica data); the rows of all the columns
     are joined in their own order, and a product without a value shows a dash. */
  lsmSpecs() {
    const base = this.querySelector('.compare-table tr[data-compare-key="lsm_specs"]');
    if (!base) return;
    const data = [...base.querySelectorAll('td:not(.td-label)')].map((td) => {
      try { return JSON.parse(td.querySelector('[data-lsm-specs]')?.textContent || 'null'); } catch (e) { return null; }
    });
    const kind = data.find((d) => d?.kind)?.kind;
    const ORDER = kind === 'lens'
      ? ['Focal length', 'Maximum aperture', 'Minimum aperture', 'Angle of view', 'Elements / groups', 'Aspherical surfaces', 'Close focus',
        'Maximum magnification', 'Aperture blades', 'Filter thread', 'Lens hood', 'Mount', 'Autofocus', 'Dimensions', 'Weight']
      : ['Sensor', 'Viewfinder', 'Rear screen', 'ISO range', 'Shutter speeds', 'Continuous shooting', 'Video', 'Autofocus',
        'Image stabilization', 'Storage', 'Battery life', 'Weatherproofing', 'Kit lens', 'Dimensions', 'Weight'];
    const rank = (label) => { const i = ORDER.indexOf(label); return i < 0 ? ORDER.length : i; };
    const labels = [];
    data.forEach((d) => (d?.rows || []).forEach(([label]) => { if (!labels.includes(label)) labels.push(label); }));
    labels.sort((x, y) => rank(x) - rank(y));
    base.hidden = true;
    let after = base;
    labels.forEach((label) => {
      const tr = document.createElement('tr');
      tr.className = 'lsm-spec-row';
      const head = document.createElement('td');
      head.className = 'td-label background-1 text-body';
      head.textContent = label;
      tr.appendChild(head);
      data.forEach((d) => {
        const td = document.createElement('td');
        const row = (d?.rows || []).find((r) => r[0] === label);
        td.textContent = row ? row[1] : '–';
        tr.appendChild(td);
      });
      after.after(tr);
      after = tr;
    });
  }

  updateProductsCount(count) {
    this.style.setProperty('--number-items', count || CompareUtils.getProductsCount());
  }

  async getCompareTableRows(products) {
    const tmps = [];
    const sectionId = this.getSectionId();
    /* LSM: a saved product is fetched by the link it was saved with; if that fails (a collection path that changed, a link from
       the old theme) it is tried once more by its plain /products/ link. One that still does not load (unpublished, renamed) is
       left out of the table and taken off the saved list, so a stale list cannot leave the drawer empty (David, 2026-10-23:
       "the compare modal doesn't work on my iphone. Just empty box with price line"). */
    const rows = async (url) => {
      try {
        const response = await fetch(CompareUtils.getTemplateUrl(url, { section_id: sectionId }));
        if (!response.ok) return null;
        const rowTemplate = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector(this.sourceSelector);
        return rowTemplate && rowTemplate.content.querySelector('td') ? rowTemplate.content.cloneNode(true) : null;
      } catch (e) {
        return null;
      }
    };
    await Promise.all(
      products.map(async (product) => {
        let found = await rows(product.url);
        const plain = (product.url || '').replace(/^.*?(\/products\/[^/?#]+).*$/, '$1');
        if (!found && plain && plain !== product.url) found = await rows(plain);
        tmps[product.id] = found;
      })
    );

    const returns = [];
    products.forEach((product) => { if (tmps[product.id]) returns.push(tmps[product.id]); });
    const gone = products.filter((product) => !tmps[product.id]);
    if (gone.length && !Shopify.designMode) {
      CompareUtils.setSelectedProducts(CompareUtils.getSelectedProducts().filter((product) => tmps[product.id]));
      this.updateProductsCount(returns.length);
    }
    return returns;
  }

  getCompareTablePlaceholderRows(count) {
    const rowTemplate = this.querySelector(this.placeholderSelector);

    const returns = [];
    for (let i = 0; i < count; i++) {
      returns.push(rowTemplate.content.cloneNode(true));
    }

    return returns;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleAfterHide();
  }

  handleAfterHide() {
    super.handleAfterHide();
    // Clean the table.
    this.querySelectorAll('.compare-table > tbody > tr').forEach((row) => {
      const nonLabelCells = row.querySelectorAll('td:not(.td-label)');
      nonLabelCells.forEach((cell) => {
        cell.remove();
      });
    });
    this.querySelectorAll('.compare-table .lsm-spec-row').forEach((row) => row.remove());
    const lsmBase = this.querySelector('.compare-table tr[data-compare-key="lsm_specs"]');
    if (lsmBase) lsmBase.hidden = false;
    const table = this.querySelector('.compare-table'); if (table) table.hidden = false;
    this.querySelector('.lsm-compare-empty')?.remove();

    this.classList.add(this.classesToRemoveOnLoad);
  }
}
customElements.define('compare-drawer', CompareDrawer);
